'use client';

import { useLoyaltyContext, usePageLayout } from '@libs/common-next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import { dataLayerPush } from '../gtm/dataLayerPush';
import type { TreatsLoginLocation } from '../gtm/types';
import type { EventTracking } from '../types';

export const useLoginEvents = () => {
    const { user } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();
    const pathname = usePathname();

    const [isLoginAction, setIsLoginAction] = useState<boolean>(false);
    const loginLocation = useRef<TreatsLoginLocation | null>(null);
    const cbFunction = useRef<() => void>(() => {});

    const trackLoginSuccessful = useCallback(() => {
        dataLayerPush({
            event: 'login-successful',
            login_status: true,
            login_method: loginLocation.current,
            loyalty_id: user?.loyaltyid,
            treats_points: memberPoints?.availablePoints,
            treats_tier: `tier ${String(memberPoints?.currentTierRank)}`,
        });
        cbFunction.current();
    }, [user?.loyaltyid, cbFunction, memberPoints]);

    const trackTreatsLogin = async (pathOverride: string | null, isFirstAutoshipEligible: boolean) => {
        const eventData: EventTracking = {
            event: 'TreatsLogin',
            loginLocation: loginLocation.current,
            previousPagePath: pathOverride ?? pathname ?? '/',
            first_autoship_eligible: isFirstAutoshipEligible,
        };

        dataLayerPush(eventData);
    };

    const trackSuccessSignIn = (
        location: TreatsLoginLocation | null,
        pathOverride: string | null = null,
        isFirstAutoshipEligible: boolean,
        callbackFunction?: () => void
    ) => {
        loginLocation.current = location;
        if (callbackFunction) {
            cbFunction.current = callbackFunction;
        }
        setIsLoginAction(true); // enable useEffect to fire login successful
        trackTreatsLogin(pathOverride, isFirstAutoshipEligible);
    };

    useEffect(() => {
        if (isLoginAction && user?.loyaltyid && memberPoints && loginLocation.current) {
            trackLoginSuccessful();
            setIsLoginAction(false);
        }
    }, [user?.loyaltyid, isLoginAction, memberPoints, trackLoginSuccessful]);

    return {
        trackSuccessSignIn,
        trackLoginSuccessful,
    };
};
